import { Button } from '../../components/Button';
import { Drawer } from '../../components/Drawer';
import { BasketContents } from '../../composites/BasketContents';
import { Recommendations } from '../../composites/Recommendations';
import { space } from '../../theme/tokens';
import {
  ActionBar,
  Body,
  RecommendationsHeading,
  RecommendationsHeadingSecondary,
} from './BasketDrawerTemplate.styles';
import type { BasketDrawerLabels, BasketDrawerTemplateProps } from './BasketDrawerTemplate.types';

const DEFAULT_LABELS: Required<BasketDrawerLabels> = {
  title: 'Your basket',
  closeLabel: 'Close basket',
  addedConfirmation: 'Added to basket',
  itemisedView: 'Itemised view',
  itineraryView: 'Itinerary view',
  total: 'Total',
  viewBasket: 'View Basket',
  checkout: 'Checkout',
  recommendationsHeadingPrimary: 'Wait.',
  recommendationsHeadingSecondary: "Don't forget to make plans...",
  selectGuestLabel: 'Select guest',
  noImageLabel: 'No image',
};

export function BasketDrawerTemplate({
  open,
  onClose,
  items,
  totalPrice,
  itemCount,
  view = 'itemised',
  onViewChange,
  guests,
  showAddedConfirmation = false,
  recommendations,
  onRecommendationSelect,
  onViewBasket,
  onCheckout,
  locale,
  labels,
}: BasketDrawerTemplateProps) {
  const resolvedLabels = { ...DEFAULT_LABELS, ...labels };
  const recs = recommendations ?? [];
  const showRecommendations = recs.length > 0;

  const recommendationsTitle = (
    <RecommendationsHeading>
      {resolvedLabels.recommendationsHeadingPrimary}{' '}
      <RecommendationsHeadingSecondary>
        {resolvedLabels.recommendationsHeadingSecondary}
      </RecommendationsHeadingSecondary>
    </RecommendationsHeading>
  );

  const footer = (
    <ActionBar>
      <Button variant="secondary" fullWidth onClick={onViewBasket}>
        {`${resolvedLabels.viewBasket} (${itemCount})`}
      </Button>
      <Button variant="primary" fullWidth onClick={onCheckout}>
        {resolvedLabels.checkout}
      </Button>
    </ActionBar>
  );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      aria-label={resolvedLabels.title}
      closeLabel={resolvedLabels.closeLabel}
      width="600px"
      mobileTopOffset={space[2]}
      backdropBlur
      footer={footer}
    >
      <Body data-testid="basket-drawer-template">
        <BasketContents
          items={items}
          totalPrice={totalPrice}
          view={view}
          onViewChange={onViewChange}
          guests={guests}
          showAddedConfirmation={showAddedConfirmation}
          locale={locale}
          labels={{
            title: resolvedLabels.title,
            itemisedView: resolvedLabels.itemisedView,
            itineraryView: resolvedLabels.itineraryView,
            addedConfirmation: resolvedLabels.addedConfirmation,
            total: resolvedLabels.total,
            noImageLabel: resolvedLabels.noImageLabel,
            selectGuestLabel: resolvedLabels.selectGuestLabel,
          }}
        />

        {showRecommendations && (
          <Recommendations
            items={recs}
            onSelect={onRecommendationSelect}
            title={recommendationsTitle}
            noImageLabel={resolvedLabels.noImageLabel}
            layout="grid"
          />
        )}
      </Body>
    </Drawer>
  );
}
